/**
 * Feedback Service
 * Handles submission of user feedback to the backend API
 */

import { loadAwsExports } from "@/lib/awsExports"

async function loadApiUrl(): Promise<string> {
  const config = await loadAwsExports()
  const base = config.appApiUrl
  if (base) return `${base}feedback`
  if (config.feedbackApiUrl) {
    console.warn(
      "aws-exports.json uses deprecated key `feedbackApiUrl`. Regenerate after next deploy: `appApiUrl` is the canonical key."
    )
    return `${config.feedbackApiUrl}feedback`
  }
  throw new Error("App API URL not configured")
}

export interface FeedbackPayload {
  sessionId: string
  message: string
  feedbackType: "positive" | "negative"
  comment?: string
}

export interface FeedbackResponse {
  success: boolean
  feedbackId: string
}

/**
 * Submit feedback to the backend API
 *
 * @param payload - Feedback data in camelCase format
 * @param idToken - Cognito ID token for authentication (required by API Gateway Cognito authorizer)
 * @returns Promise with feedback response
 */
export async function submitFeedback(
  payload: FeedbackPayload,
  idToken: string
): Promise<FeedbackResponse> {
  try {
    // Load the API URL dynamically
    const apiUrl = await loadApiUrl()

    const response = await fetch(apiUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify(payload),
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
    }

    const data: FeedbackResponse = await response.json()
    return data
  } catch (error) {
    console.error("Error submitting feedback:", error)
    throw error
  }
}
